import { ApiService } from './ApiService';
import { DeviceDetect } from '../utils/DeviceDetect';
import { Router } from '../router/Router';

/**
 * Handle the Probe Service.
 */
export class ProbeService {
  /**
   * Current probe timer.
   *
   * @type {int}
   */
  static currentProbeStartTime;

  /**
   * List of probes.
   *
   * @type Object
   */
  static probesList = [];

  /**
   * Max size of probes list before send the list to the api.
   * Special case for IE browser which does not supports the unload event.
   *
   * @type {number}
   */
  static maxProbe = 1;

  /**
   * Init probe service.
   *
   * @param {number} probesListMaxSize - Max size of probes list before send the list to the api.
   */
  static init(probesListMaxSize) {
    ProbeService.maxProbe = !DeviceDetect.isIE() && probesListMaxSize > 0 ? probesListMaxSize : 1;
    ProbeService.resetProbes();
  }

  /**
   * Start the probe timer.
   */
  static startTimer() {
    ProbeService.currentProbeStartTime = new Date().getTime();
  }

  /**
   * Stop the probe timer and add it in the probes list.
   */
  static endTimer() {
    if (ProbeService.currentProbeStartTime) {
      const elapsed = new Date().getTime() - ProbeService.currentProbeStartTime;

      ProbeService.addProbe(elapsed);
    }
  }

  /**
   * Add probe data in the list of probes to send and triggers sendProbe if the limit is reached (maxProbe).
   *
   * @param {number} elapsed - Current probe elapsed time.
   * @returns {Promise} The promise of the sent probe.
   */
  static addProbe(elapsed) {
    const data = {
      name: Router.currentRoute,
      value: elapsed,
      action: Router.currentRoute,
      service: ApiService.service,
      os: navigator.oscpu || navigator.platform,
      resolution: `${window.innerWidth}x${window.innerHeight}`,
      ontouch: DeviceDetect.isTouch(),
      webViewMode: Boolean(Eui.isWebViewMode),
    };

    ProbeService.probesList.push(data);

    if (ProbeService.probesList.length >= ProbeService.maxProbe) {
      return ProbeService.sendProbe();
    }
    return 0;
  }

  /**
   * Reset the list of probes and the current probe timer.
   */
  static resetProbes() {
    ProbeService.currentProbeStartTime = undefined;
    ProbeService.probesList = [];
  }

  /**
   * Send the probe to the api and reset list of probes and current probe timer.
   *
   * @returns {Promise} The promise of the sent probe.
   */
  static sendProbe() {
    if (!ProbeService.probesList.length) {
      // reset the current probe timer, list of probes is already empty
      ProbeService.resetProbes();

      return Promise.resolve();
    }

    const probeSchema = ApiService.apiSchema.probe;

    // make a silent api request => no error message will be displayed in case of api error
    return ApiService.makeRequest({
      url: probeSchema.url,
      method: probeSchema.method,
      body: {
        probes: ProbeService.probesList,
      },
    }, true)
      .catch(() => undefined)
      // always reset probes even if the api returns an error
      .then(() => ProbeService.resetProbes());
  }
}
